import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AREA = { "centre-ville": "Centre-ville", "petite-couronne": "Petite couronne", "grande-couronne": "Grande couronne" };
const APT = { studio: "Studio", t2: "2 pièces", t3: "3 pièces", t4plus: "4 pièces et plus" };

export const ForwardButton = ({ onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-3 py-2 rounded-md bg-[#8C6C50] text-white text-sm inline-flex items-center gap-1.5 hover:bg-[#6B5B49] transition-colors"
    data-testid={testid}
  >
    <Send className="w-3.5 h-3.5" /> Envoyer aux résidences
  </button>
);

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

const ANSWERS = [["pending", "En attente"], ["yes", "Oui, disponible"], ["no", "Non"]];

function ResidenceTag({ init, unseen, lid }) {
  if (init?.source !== "residence") return null;
  const counter = init.counter ? [init.unit === "month" && init.price != null && `${Math.round(init.price)} € / mois`, init.price_night != null && `${Math.round(init.price_night)} € / nuit`].filter(Boolean).join(" · ") : "";
  return (
    <span className="inline-flex items-center gap-1" data-testid={`residence-answer-tag-${lid}`}>
      {unseen && <span className="px-1.5 py-0.5 rounded bg-[#C2410C] text-white text-[10px] font-semibold uppercase tracking-wider">Nouveau</span>}
      <span className="text-[#2B3029]">· répondu par la résidence{init.counter ? ` (contre-proposition${counter ? ` · ${counter}` : ""})` : ""}</span>
    </span>
  );
}

function ResponseRow({ lid, g, init, reminded, save, unseen }) {
  const [v, setV] = useState({ answer: init?.answer || "pending", price: init?.price ?? "", unit: init?.unit || "month" });
  const commit = (next) => {
    setV(next);
    save(lid, next);
  };
  const tone = v.answer === "yes" ? "bg-[#EEF3EC] text-[#4F6B4A]" : v.answer === "no" ? "bg-[#F7EDEC] text-[#8A3B32]" : "bg-[#F5EFE6] text-[#6B5B49]";
  const r = reminded;
  return (
    <li className={`text-xs px-2 py-1.5 rounded flex flex-wrap items-center gap-x-3 gap-y-1.5 ${tone}`} data-testid={`forwarded-row-${lid}`}>
      <span className="font-medium">{g.brand ? `${g.brand} · ` : ""}{g.title}</span>
      <span>· envoyée à {g.contacts.size} contact(s) le {fmt(g.first)}</span>
      {r && <span className="text-[#8C6C50]" data-testid={`reminded-${lid}`}>· relancée le {fmt(r.sent_at)}{r.contacts?.length === 0 ? " (échec d'envoi)" : ""}</span>}
      {!r && v.answer === "pending" && <span className="text-[#999]">· relance auto 12 h après l'envoi</span>}
      {init?.updated_at && init.answer !== "pending" && <span className="text-[#2B3029]" data-testid={`response-at-${lid}`}>· réponse le {fmt(init.updated_at)}</span>}
      <ResidenceTag init={init} unseen={unseen} lid={lid} />
      <span className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
        Réponse
        <select value={v.answer} onChange={(e) => commit({ ...v, answer: e.target.value })} className="bg-white border border-[#E5E1D8] rounded px-1.5 py-0.5 text-[#1A1A1A]" data-testid={`response-answer-${lid}`}>
          {ANSWERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        Tarif
        <input type="number" min="0" step="1" value={v.price} onChange={(e) => setV({ ...v, price: e.target.value })} onBlur={() => save(lid, v)} placeholder="—" className="w-20 bg-white border border-[#E5E1D8] rounded px-1.5 py-0.5 text-[#1A1A1A]" data-testid={`response-price-${lid}`} />
        €
        <select value={v.unit} onChange={(e) => commit({ ...v, unit: e.target.value })} className="bg-white border border-[#E5E1D8] rounded px-1.5 py-0.5 text-[#1A1A1A]" data-testid={`response-unit-${lid}`}>
          <option value="month">/ mois</option>
          <option value="night">/ nuit</option>
        </select>
      </span>
    </li>
  );
}

export const ForwardedInfo = ({ request, kind, onChange }) => {
  const items = request.forwarded;
  if (!items?.length) return null;
  const byListing = {};
  items.forEach((i) => {
    const g = (byListing[i.listing_id] = byListing[i.listing_id] || { title: i.listing_title, brand: (request.brands || {})[i.listing_id], contacts: new Set(), first: i.sent_at });
    g.contacts.add(i.contact_email);
    if (i.sent_at < g.first) g.first = i.sent_at;
  });
  const reminded = Object.fromEntries((request.reminded || []).map((r) => [r.listing_id, r]));
  const responses = { ...Object.fromEntries((request.answered || []).map((lid) => [lid, { answer: "yes" }])), ...(request.responses || {}) };
  const save = async (lid, v) => {
    try {
      await axios.patch(`${API}/admin/${kind}/${request.id}/response`,
        { listing_id: lid, answer: v.answer, unit: v.unit, price: v.price === "" ? null : Number(v.price) }, { withCredentials: true });
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Enregistrement impossible");
    }
  };
  return (
    <ul className="mt-2 space-y-1.5" data-testid="forwarded-info">
      {Object.entries(byListing).map(([lid, g]) => (
        <ResponseRow key={`${lid}-${responses[lid]?.updated_at || ""}`} lid={lid} g={g} init={responses[lid]} reminded={reminded[lid]} save={save} unseen={(request.unseen || []).includes(lid)} />
      ))}
    </ul>
  );
};

function RecipientRow({ l, checked, onToggle, already }) {
  return (
    <label className="flex items-start gap-3 p-3 border border-[#E5E1D8] rounded-md cursor-pointer hover:bg-[#F5F3ED] transition-colors" data-testid={`forward-recipient-${l.id}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1 accent-[#8C6C50]" data-testid={`forward-recipient-checkbox-${l.id}`} />
      <div className="text-sm">
        <div className="font-medium text-[#1A1A1A]">{l.title}</div>
        <div className="text-[#666]">{l.contact_name ? `${l.contact_name} · ` : ""}{(l.contact_emails?.length ? l.contact_emails : [l.contact_email]).join(", ")}</div>
        <div className="text-xs text-[#999] mt-0.5">
          {l.city} · {AREA[l.area] || "Emplacement non renseigné"} · {APT[l.apartment_type] || "Type non renseigné"} · {l.capacity} pers.
          {already && <span className="ml-2 text-[#4F6B4A]">· déjà envoyée</span>}
        </div>
      </div>
    </label>
  );
}

export function ForwardDialog({ kind, request, onClose, onSent }) {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const sentIds = new Set((request.forwarded || []).map((f) => f.listing_id));

  useEffect(() => {
    axios.get(`${API}/admin/${kind}/${request.id}/recipients`, { withCredentials: true })
      .then(({ data }) => { setList(data); setSelected(data.filter((l) => !sentIds.has(l.id)).map((l) => l.id)); })
      .catch(() => { setList([]); toast.error("Impossible de charger les résidences"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, request.id]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/${kind}/${request.id}/forward`, { listing_ids: selected }, { withCredentials: true });
      if (data.sent.length) toast.success(`Demande envoyée à ${data.sent.length} contact(s)`);
      if (data.paused) toast.error(data.paused, { duration: 10000 });
      else if (data.failed.length) toast.error(`Échec d'envoi : ${data.failed.join(", ")}`, { duration: 8000 });
      onSent();
      if (data.sent.length) onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="forward-dialog">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E1D8]">
          <div>
            <h3 className="font-serif text-2xl text-[#1A1A1A]">Envoyer aux résidences</h3>
            <p className="text-xs text-[#666] mt-1">
              Les coordonnées du client ne sont pas transmises et le budget n'est pas communiqué. Les réponses arrivent sur votre adresse administrateur.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#EFECE5] rounded-md" data-testid="forward-dialog-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-2 flex-1" data-testid="forward-recipients-list">
          {list === null && <div className="text-sm text-[#666]">Recherche des résidences…</div>}
          {list?.length === 0 && (
            <div className="text-sm text-[#666]" data-testid="forward-no-match">
              Aucune annonce ne correspond aux critères (ville, emplacement, type, capacité, durée).
            </div>
          )}
          {list?.map((l) => (
            <RecipientRow key={l.id} l={l} checked={selected.includes(l.id)} onToggle={() => toggle(l.id)} already={sentIds.has(l.id)} />
          ))}
        </div>
        <div className="p-6 border-t border-[#E5E1D8] flex items-center justify-between gap-4">
          <span className="text-sm text-[#666]" data-testid="forward-selected-count">{selected.length} sélectionnée(s)</span>
          <button
            onClick={send}
            disabled={sending || !selected.length}
            className="btn-primary px-6 py-2.5 rounded-full text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="forward-send-btn"
          >
            <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
}
